import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiResponse } from '@/types/api';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://chatapi.yurenjian.cn';

// 微服务地址（开发环境使用相对路径走 Vite 代理，生产环境统一走 chatapi.yurenjian.cn 反向代理）
const IS_DEV = import.meta.env.DEV;
export const SERVICE_URLS = {
  user: IS_DEV ? '' : (import.meta.env.VITE_USER_SERVICE || 'https://chatapi.yurenjian.cn'),
  scene: IS_DEV ? '' : (import.meta.env.VITE_SCENE_SERVICE || 'https://chatapi.yurenjian.cn'),
  chat: IS_DEV ? '' : (import.meta.env.VITE_CHAT_SERVICE || 'https://chatapi.yurenjian.cn'),
  progress: IS_DEV ? '' : (import.meta.env.VITE_PROGRESS_SERVICE || 'https://chatapi.yurenjian.cn'),
  billing: IS_DEV ? '' : (import.meta.env.VITE_BILLING_SERVICE || 'https://chatapi.yurenjian.cn'),
  tts: IS_DEV ? '' : (import.meta.env.VITE_TTS_SERVICE || 'https://chatapi.yurenjian.cn'),
};

// Token 管理
let _accessToken = localStorage.getItem('accessToken') || '';
let _refreshToken = localStorage.getItem('refreshToken') || '';

export function getToken(): string {
  if (!_accessToken) {
    _accessToken = localStorage.getItem('accessToken') || '';
  }
  return _accessToken;
}

export function setTokens(access: string, refresh: string): void {
  _accessToken = access;
  _refreshToken = refresh;
  localStorage.setItem('accessToken', access);
  localStorage.setItem('refreshToken', refresh);
}

export function clearTokens(): void {
  _accessToken = '';
  _refreshToken = '';
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
}

// Token 刷新队列
let isRefreshing = false;
let refreshQueue: Array<{ resolve: (token: string) => void; reject: (err: Error) => void }> = [];

async function refreshAccessToken(): Promise<{ token: string; refresh_token: string }> {
  if (!_refreshToken) throw new Error('No refresh token');
  const res = await axios.post<ApiResponse<{ token: string; refresh_token: string }>>(
    `${SERVICE_URLS.user}/api/v1/auth/refresh`,
    { refresh_token: _refreshToken },
  );
  if (res.data.code === 0 && res.data.data) {
    return res.data.data;
  }
  throw new Error('Refresh failed');
}

// 创建 API 客户端（每个微服务独立实例，指向不同 baseURL）
function createApiClient(baseURL: string): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout: 15000,
    headers: { 'Content-Type': 'application/json' },
  });

  // 请求拦截器：自动注入 token
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // 响应拦截器：401 自动刷新 token
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiResponse>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshQueue.push({
              resolve: (token: string) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                resolve(client(originalRequest));
              },
              reject,
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const { token, refresh_token } = await refreshAccessToken();
          setTokens(token, refresh_token);
          refreshQueue.forEach((r) => r.resolve(token));
          refreshQueue = [];
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return client(originalRequest);
        } catch (refreshError) {
          refreshQueue.forEach((r) => r.reject(refreshError as Error));
          refreshQueue = [];
          clearTokens();
          // 不自动跳转，让业务层决定
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    },
  );

  return client;
}

// 各微服务独立客户端
export const userClient = createApiClient(SERVICE_URLS.user);
export const sceneClient = createApiClient(SERVICE_URLS.scene);
export const chatClient = createApiClient(SERVICE_URLS.chat);
export const progressClient = createApiClient(SERVICE_URLS.progress);
export const ttsClient = createApiClient(SERVICE_URLS.tts);
export const billingClient = createApiClient(SERVICE_URLS.billing);

// 通用 HTTP 客户端（指向 user-service 作为默认）
export const http = userClient;
